import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import PortalShell from '@/components/portal/PortalShell';
import PublicHero from '@/components/portal/PublicHero';
import { EmptyState } from '@/components/eds';
import { contentService, type PricingPlan } from '@/services/contentService';

// Access Levels composition. Presentation-only; levels are driven by
// contentService.listPricing() (geo-api → governed published fallback).
// P9B: these are ACCESS CLASSIFICATIONS (Public / Investor / Government & Agency),
// NOT commercial subscription tiers. No prices and no sales/SaaS positioning.

// Per-level call-to-action derived from the level (presentation-only routing).
function ctaFor(plan: PricingPlan): { label: string; to: string; primary: boolean } {
  if (plan.highlighted) return { label: 'Investor Access', to: '/investor-login', primary: true };
  if (/public|open/i.test(plan.name) || /open/i.test(plan.price)) {
    return { label: 'Explore Public Information', to: '/', primary: false };
  }
  // Government & Agency access is provisioned by GIISDEC via the contact channel.
  return { label: 'Request Access', to: '/contact', primary: false };
}

const Pricing: React.FC = () => {
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  useEffect(() => { contentService.listPricing().then(setPlans); }, []);

  const tiers = Array.isArray(plans) ? plans : [];

  return (
    <PortalShell>
      <PublicHero
        badge="Access Levels"
        title={<>Access that matches<br /><span className="text-eds-mint">your mandate</span></>}
        subtitle="From open public information to investor and government/agency access. These are access levels, not commercial plans."
      />

      <section className="py-16 md:py-20 bg-eds-bg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {tiers.length === 0 ? (
            <EmptyState
              title="Access levels are being finalised"
              message="Approved access levels will appear here once released by GIISDEC."
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
              {tiers.map((p) => {
                const cta = ctaFor(p);
                return (
                  <div
                    key={p.id}
                    className={`relative rounded-eds-lg p-7 bg-eds-surface shadow-eds-sm ${
                      p.highlighted
                        ? 'border-2 border-eds-emerald ring-2 ring-eds-emerald/20 md:-translate-y-2 shadow-eds-md'
                        : 'border border-eds-border'
                    }`}
                  >
                    {p.highlighted && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center px-3 py-1 rounded-eds-pill bg-eds-primary text-eds-primary-fg text-[11px] font-semibold uppercase tracking-widest">
                        Primary investor access
                      </span>
                    )}
                    <h3 className="font-eds-display text-lg font-bold text-eds-fg">{p.name}</h3>
                    <div className="mt-3 flex items-baseline gap-2">
                      <span className="font-eds-display text-4xl font-bold text-eds-fg">{p.price}</span>
                      {p.cadence && <span className="text-sm text-eds-muted-fg">{p.cadence}</span>}
                    </div>
                    <ul className="mt-6 space-y-3">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-sm text-eds-fg">
                          <Check className="w-4 h-4 text-eds-emerald-2 mt-0.5 flex-shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={cta.to}
                      className={`mt-7 inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-eds-pill text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg ${
                        cta.primary
                          ? 'bg-eds-primary text-eds-primary-fg hover:bg-eds-emerald-2'
                          : 'border border-eds-border text-eds-fg hover:bg-eds-surface-2'
                      }`}
                    >
                      {cta.label} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                );
              })}
            </div>
          )}

          <p className="mt-10 text-center text-xs text-eds-muted-fg">
            Access levels describe who can use each part of the platform. Access is governed
            server-side by GIISDEC; government and agency accounts are provisioned by GIISDEC.
          </p>
        </div>
      </section>
    </PortalShell>
  );
};

export default Pricing;
